import {
  CheckOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import {
  Col,
  Form,
  Popover,
  Radio,
  Row,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { ScopeTabs } from 'components/Custom/tabs';
import { ReactComponent as CrossIcon } from 'assets/images/icons/crossIcon.svg';
import { ReactComponent as EditIcon } from 'assets/images/icons/editIcon.svg';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as UserIcon } from 'assets/images/icons/userIcon.svg';
import moment from 'moment';
import { AccountTypeTag } from 'components/Custom/AccountTypeTag';
import React from 'react';
import { User, UserLicence } from 'store/auth/models';
import { Country, CountryFlag } from 'store/common/models';
import { LoginDetails } from 'store/user/models';

import {
  ContextButton,
  IconContextButton,
  IconLogout,
  ScopeButton,
  USER_MENU_ICON_STYLE,
} from '@monorepo/shared-lib';
import ImgWithQboSignIn from 'assets/images/qb-icon.png';
import ImgWithXeroSignIn from 'assets/images/xero_icon.png';
import ImgWithZohoSignIn from 'assets/images/zoho_icon.png';
import { ChangePasswordForm } from 'components/AccountComponent/change-password';
import { StyledCard } from 'components/Custom/card';
import { SSOImg } from 'components/Custom/images';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { CardTitleSpan, ColoredTag, GraySpan } from 'components/Custom/spans';
import { ProfileWrapper } from 'components/Custom/wrappers';
import { History } from 'components/UsersComponent/history';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { logoutRequest } from 'store/auth/actions';
import { AccountType, Currency, SubscriptionPlan } from 'store/api/enums';
import { isZohoEnabled } from 'utils/feature-flag-utils';
import { BillingForm } from './billing-form';

import { LicenseStatus, LicenseType } from 'store/app/enums';
import { Plan } from 'store/billing/models';
import { BillingFormRequest } from 'store/billing/models/billingFormRequest';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';
import defaultUserPicture from '../../assets/images/default-image.svg';
import { ProfileCard } from './profile-card';
import { UpgradeCard } from './upgrade-card';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import {
  APP_THEME,
  applyThemePreference,
  getStoredThemePreference,
  persistThemePreference,
  normalizeThemePreference,
  ThemePreference,
} from 'utils/theme-preference';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
export const billingPageLink = '/billing';
export const pricingPageLink = '/pricing';

const PROFILE_TAB_ACCOUNT_SECURITY = 'account-security';
const PROFILE_TAB_BILLING_DETAILS = 'billing-details';
const PROFILE_TAB_LOGIN_HISTORY = 'login-history';

interface AccountComponentProps {
  loading: boolean;
  currentUser?: User;
  selectedAccount?: User;
  history?: LoginDetails[];
  connectXeroAccount: () => void;
  connectQboAccount: () => void;
  connectZohoAccount: () => void;
  disconnectXeroAccount: () => void;
  disconnectQboAccount: () => void;
  disconnectZohoAccount: () => void;
  disconnectGoogleAccount: () => void;
  openMFAWindow: () => void;
  removeMfa: (userId: string) => void;
  deleteProfile: () => void;
  savePassword: (
    oldPassword: string,
    newPassword: string,
    newPasswordConfirm: string
  ) => void;
  openAccessRights: (accountId: string) => void;
  openEditImageWindow: () => void;
  onDeleteUser: (userToDeleteId: string) => void;
  countries: Country[];
  countriesExternalData: CountryFlag[];
  openEditDetails: () => void;
  openChangeOwnerForm: () => void;
  redirectPage: (url: string) => void;
  //isVATNumberValid: boolean;
  changeCurrency: (newCurrency: Currency) => void;
  //validateVatNumber: (countryCode: string, taxNumber: string) => void;
  //resetValidationVatNumber?: () => void;
  billingDetails?: BillingFormRequest;
  saveBillingDetails: (billingDetails: BillingFormRequest) => void;
  menuSize: string;
  userLicence?: UserLicence;
  currentSubscription?: CurrentSubscription;
  getFeaturesOfPlans?: () => Plan[];
}

export const AccountComponent: React.FC<AccountComponentProps> = ({
  loading,
  currentUser,
  selectedAccount,
  history,
  connectXeroAccount,
  connectQboAccount,
  connectZohoAccount,
  disconnectXeroAccount,
  disconnectQboAccount,
  disconnectZohoAccount,
  openMFAWindow,
  removeMfa,
  deleteProfile,
  savePassword,
  openAccessRights,
  openEditImageWindow,
  onDeleteUser,
  countries,
  countriesExternalData,
  openEditDetails,
  openChangeOwnerForm,
  redirectPage,
  billingDetails,
  saveBillingDetails,
  userLicence,
  currentSubscription,
  getFeaturesOfPlans,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const preferences = usePreferences();
  const [formPassword] = Form.useForm();
  const onXeroAccountConnect = () => connectXeroAccount();
  const onQboAccountConnect = () => connectQboAccount();
  const onZohoAccountConnect = () => connectZohoAccount();
  const onXeroAccountDisconnect = () => disconnectXeroAccount();
  const onQboAccountDisconnect = () => disconnectQboAccount();
  const onZohoAccountDisconnect = () => disconnectZohoAccount();
  const onEnableMFAClick = () => openMFAWindow();
  const onRemoveMfaClick = (userId: string) => removeMfa(userId);
  const [showCard, setShowCard] = React.useState(true);
  const [billingForm] = Form.useForm();
  const [isAllowed, setIsAllowed] = React.useState(false);
  const [userVatNumber, setUserVatNumber] = React.useState<string>('');
  const pwaMobileNav = usePwaMobileNavEnabled();
  const [themePreference, setThemePreference] =
    React.useState<ThemePreference>(getStoredThemePreference);

  const values = Form.useWatch([], billingForm);

  React.useEffect(() => {
    let isMounted = true;
    billingForm
      .validateFields({ validateOnly: true })
      .then(() => {
        if (isMounted) {
          setIsAllowed(true);
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsAllowed(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [billingForm, values]);

  React.useEffect(() => {
    if (billingDetails) {
      billingForm.setFieldsValue({
        addressLine1: billingDetails.addressLine1,
        addressLine2: billingDetails.addressLine2,
        town: billingDetails.town,
        country: billingDetails.country,
        zipCode: billingDetails.zipCode,
        phonePrefix: billingDetails.phonePrefix,
        phone: billingDetails.phone,
        email: billingDetails.email,
        currency: billingDetails.currency,
        vatNumber: billingDetails.vatNumber,
        companyName: billingDetails.companyName,
      });
      setUserVatNumber(billingDetails.vatNumber);
    }
  }, [billingDetails]);

  const profilePicture: string = selectedAccount
    ? selectedAccount?.profilePicture
      ? selectedAccount.profilePicture
      : defaultUserPicture
    : defaultUserPicture;

  const isCurrentUser = !selectedAccount?.userUuid
    ? !!currentUser?.userUuid
    : selectedAccount.userUuid === currentUser?.userUuid;
  const userUuid = currentUser?.userUuid ?? '';

  React.useEffect(() => {
    if (!isCurrentUser || !userUuid) return;
    const storedTheme = preferences(
      PreferenceActions.GET,
      PreferenceSections.APP,
      { name: APP_THEME },
      userUuid
    );
    const normalizedTheme = normalizeThemePreference(storedTheme);
    setThemePreference(normalizedTheme);
    applyThemePreference(normalizedTheme);
    persistThemePreference(normalizedTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from persisted prefs only
  }, [userUuid, isCurrentUser, currentUser?.prefDetails?.[1]]);

  const onThemePreferenceChange = (value: ThemePreference) => {
    setThemePreference(value);
    applyThemePreference(value);
    persistThemePreference(value);
    if (!userUuid) return;
    preferences(
      PreferenceActions.SET,
      PreferenceSections.APP,
      { name: APP_THEME, value },
      userUuid
    );
  };

  const renderAccountCard = () => (
    <StyledCard $stopshadowbox className="profile-settings-card">
      <div className="a11y-control-row a11y-control-row--action">
        <div>
          <div className="a11y-control-label">{t('common.account')}</div>
          <div className="a11y-control-help">
            {`${t('users.ip-address')}: ${history && history[0].loginIP}`}
            {' · '}
            {`${t('users.last-login')}: ${
              history &&
              moment(history[0]?.loginTimestamp).format('DD-MM-YYYY hh:mm:ss')
            }`}
          </div>
        </div>
        <div className="profile-settings-action">
          <a
            target="_blank"
            href={`https://whatismyipaddress.com/ip/${
              history && history[0].loginIP
            }`}
            rel="noreferrer"
          >
            <ScopeButton type="default" width="auto" height="32px">
              {t('users.verify-location')}
            </ScopeButton>
          </a>
        </div>
      </div>
    </StyledCard>
  );

  const renderUserPermissionsCard = () => (
    <StyledCard $stopshadowbox className="profile-settings-card">
      <div className="a11y-control-row a11y-control-row--action">
        <div>
          <div className="a11y-control-label">{t('users.user-permissions')}</div>
          <div className="a11y-control-help">
            {t('other.entity-access-description')}
          </div>
        </div>
        <div className="profile-settings-action">
          <ScopeButton
            type="default"
            width="auto"
            height="32px"
            onClick={() => openAccessRights(selectedAccount?.accountUuid || '')}
          >
            {t('users.access-rights')}
          </ScopeButton>
        </div>
      </div>
    </StyledCard>
  );

  const renderAssociatedAccountsCard = () => {
    return (
      <StyledCard $stopshadowbox className="profile-settings-card">
        <div className="a11y-control-label">{t('users.associated-accounts')}</div>
        <div className="a11y-control-help" style={{ marginBottom: 12 }}>
          {t('users.associated-accounts-desc')}
        </div>
        <div className="profile-settings-subrow">
          <Space style={{ columnGap: 20, minWidth: 0 }}>
                <SSOImg
                  src={ImgWithXeroSignIn}
                  alt={t('common.xero')}
                  width="31px"
                  $padding="3px"
                  $noAbsolute
                />
                <CardTitleSpan style={{ fontSize: '14px', fontWeight: 500 }}>
                  {t('users.xero-accounting')}
                </CardTitleSpan>
              </Space>
              <div className="profile-settings-action">
              {isCurrentUser ? (
                selectedAccount?.xeroEnabled ? (
                  <StyledPopconfirm
                    title={t('other.disconnect-account')}
                    description={
                      <span>
                        {t('other.disconnect-xero-confirm')}
                      </span>
                    }
                    onConfirm={onXeroAccountDisconnect}
                    placement="top"
                    okText={t('common.yes')}
                    cancelText={t('common.cancel')}
                  >
                    <ScopeButton type="default" width="auto" height="32px">
                      {t('common.disconnect')}
                    </ScopeButton>
                  </StyledPopconfirm>
                ) : (
                  <ScopeButton
                    type="primary"
                    width="auto"
                    height="32px"
                    onClick={onXeroAccountConnect}
                  >
                    {t('common.connect')}
                  </ScopeButton>
                )
              ) : (
                <div
                  style={{
                    width: '154px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  {selectedAccount?.xeroEnabled ? (
                    <ColoredTag
                      color="var(--color-status-success, #058181)"
                      bgcolor="var(--color-status-success-bg, #E9F6F7)"
                      bordered={true}
                      bordercolor="#DBEAE3"
                      icon={<CheckOutlined />}
                    >
                      {t('common.enabled')}
                    </ColoredTag>
                  ) : (
                    <ColoredTag
                      color='var(--color-text-muted, #5a6a6a)'
                      bgcolor="#F3F7F8"
                      bordered={true}
                      bordercolor="#DBEAE3"
                    >
                      {t('common.not-enabled')}
                    </ColoredTag>
                  )}
                </div>
              )}
              </div>
            </div>

            <div className="profile-settings-subrow">
              <Space style={{ columnGap: 20, minWidth: 0 }}>
                <SSOImg
                  src={ImgWithQboSignIn}
                  alt={t('common.quickbooks')}
                  width="31px"
                  $padding="3px"
                  $noAbsolute
                />
                <CardTitleSpan style={{ fontSize: '14px', fontWeight: 500 }}>
                  {t('users.qbo-accounting')}
                </CardTitleSpan>
              </Space>
              <div className="profile-settings-action">
              {isCurrentUser ? (
                selectedAccount?.quickbooksEnabled ? (
                  <StyledPopconfirm
                    title={t('other.disconnect-account')}
                    description={
                      <span>
                        {t('other.disconnect-quickbooks-confirm')}
                      </span>
                    }
                    onConfirm={onQboAccountDisconnect}
                    placement="top"
                    okText={t('common.yes')}
                    cancelText={t('common.cancel')}
                  >
                    <ScopeButton type="default" width="auto" height="32px">
                      {t('common.disconnect')}
                    </ScopeButton>
                  </StyledPopconfirm>
                ) : (
                  <ScopeButton
                    type="primary"
                    width="auto"
                    height="32px"
                    onClick={onQboAccountConnect}
                  >
                    {t('common.connect')}
                  </ScopeButton>
                )
              ) : (
                <div
                  style={{
                    width: '154px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  {selectedAccount?.quickbooksEnabled ? (
                    <ColoredTag
                      color="var(--color-status-success, #058181)"
                      bgcolor="var(--color-status-success-bg, #E9F6F7)"
                      bordered={true}
                      bordercolor="#DBEAE3"
                      icon={<CheckOutlined />}
                    >
                      {t('common.enabled')}
                    </ColoredTag>
                  ) : (
                    <ColoredTag
                      color='var(--color-text-muted, #5a6a6a)'
                      bgcolor="#F3F7F8"
                      bordered={true}
                      bordercolor="#DBEAE3"
                    >
                      {t('common.not-enabled')}
                    </ColoredTag>
                  )}
                </div>
              )}
              </div>
            </div>
            {isZohoEnabled() && (
              <div className="profile-settings-subrow">
              <Space style={{ columnGap: 20, minWidth: 0 }}>
                  <SSOImg
                    src={ImgWithZohoSignIn}
                    alt={t('common.zoho')}
                    width="31px"
                    $padding="3px"
                    $noAbsolute
                  />
                  <CardTitleSpan style={{ fontSize: '14px', fontWeight: 500 }}>
                    {t('common.zoho')}
                  </CardTitleSpan>
                </Space>
                <div className="profile-settings-action">
                {isCurrentUser ? (
                  selectedAccount?.zohoEnabled ? (
                    <StyledPopconfirm
                      title={t('other.disconnect-account')}
                      description={
                        <span>{t('other.disconnect-zoho-confirm')}</span>
                      }
                      onConfirm={onZohoAccountDisconnect}
                      placement="top"
                      okText={t('common.yes')}
                      cancelText={t('common.cancel')}
                    >
                      <ScopeButton type="default" width="auto" height="32px">
                        {t('common.disconnect')}
                      </ScopeButton>
                    </StyledPopconfirm>
                  ) : (
                    <ScopeButton
                      type="primary"
                      width="auto"
                      height="32px"
                      onClick={onZohoAccountConnect}
                    >
                      {t('common.connect')}
                    </ScopeButton>
                  )
                ) : (
                  <div
                    style={{
                      width: '154px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {selectedAccount?.zohoEnabled ? (
                      <ColoredTag
                        color="var(--color-status-success, #058181)"
                        bgcolor="var(--color-status-success-bg, #E9F6F7)"
                        bordered={true}
                        bordercolor="#DBEAE3"
                        icon={<CheckOutlined />}
                      >
                        {t('common.enabled')}
                      </ColoredTag>
                    ) : (
                      <ColoredTag
                        color="var(--color-text-muted, #5a6a6a)"
                        bgcolor="#F3F7F8"
                        bordered={true}
                        bordercolor="#DBEAE3"
                      >
                        {t('common.not-enabled')}
                      </ColoredTag>
                    )}
                  </div>
                )}
                </div>
              </div>
            )}
      </StyledCard>
    );
  };

  const renderMFACard = () => (
    <StyledCard $stopshadowbox className="profile-settings-card">
      <div className="a11y-control-row">
        <div>
          <div className="a11y-control-label">{t('users.mfa-authentication')}</div>
          <div className="a11y-control-help">
            {t('users.mfa-authentication-desc')}
          </div>
        </div>
        <Space className="a11y-preference-switch">
          <GraySpan>
            {selectedAccount?.mfa ? t('common.on') : t('common.off')}
          </GraySpan>
          <Tooltip
            title={
              selectedAccount?.mfa
                ? t('common.toggle-on-click-to-turn-off')
                : t('common.toggle-off-click-to-turn-on')
            }
          >
            <span>
              <Switch
                aria-label={t('users.mfa-authentication')}
                className={selectedAccount?.mfa ? 'switch-checked' : ''}
                checked={selectedAccount?.mfa}
                disabled={
                  !isCurrentUser &&
                  !selectedAccount?.mfa &&
                  !currentUser?.canManageUsers
                }
                onChange={() => {
                  if (selectedAccount?.mfa) {
                    onRemoveMfaClick(selectedAccount.userUuid);
                  } else if (isCurrentUser) {
                    onEnableMFAClick();
                  }
                }}
              />
            </span>
          </Tooltip>
        </Space>
      </div>
    </StyledCard>
  );

  const renderPasswordCard = () => (
    <StyledCard $stopshadowbox className="profile-settings-card">
      <div className="a11y-control-label">{t('users.change-password')}</div>
      <div style={{ marginTop: 16, width: '100%' }}>
        <ChangePasswordForm
          hasPassword={currentUser?.hasPassword}
          savePassword={savePassword}
          form={formPassword}
        />
      </div>
    </StyledCard>
  );

  const renderThemeCard = () => (
    <StyledCard $stopshadowbox className="profile-settings-card">
      <div className="a11y-control-row a11y-control-row--radio">
        <div>
          <div className="a11y-control-label">{t('users.theme')}</div>
          <div className="a11y-control-help">{t('users.theme-help')}</div>
        </div>
        <Radio.Group
          value={themePreference}
          onChange={(e) => onThemePreferenceChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="light">{t('users.theme-light')}</Radio.Button>
          <Radio.Button value="dark">{t('users.theme-dark')}</Radio.Button>
        </Radio.Group>
      </div>
    </StyledCard>
  );

  const renderAccountAndSecurityTab = () => (
    <Space
      direction="vertical"
      size={16}
      className="profile-settings-stack"
      style={{ marginTop: pwaMobileNav ? 0 : '25px' }}
    >
      {renderAccountCard()}
      {selectedAccount &&
        selectedAccount.accountType &&
        AccountType[selectedAccount.accountType] !== AccountType.STAFF &&
        AccountType[selectedAccount.accountType] !== AccountType.CLIENT &&
        renderUserPermissionsCard()}
      {renderAssociatedAccountsCard()}
      {!pwaMobileNav && renderMFACard()}
      {isCurrentUser && renderThemeCard()}
      {isCurrentUser && !pwaMobileNav && renderPasswordCard()}
    </Space>
  );

  const renderRole = (content: string) => (
    <AccountTypeTag accountTypeKey={content} t={t} />
  );

  const renderUserSubscription = () => {
    if (
      !selectedAccount ||
      !(
        AccountType[selectedAccount.accountType] === AccountType.OWNER ||
        AccountType[selectedAccount.accountType] === AccountType.STAFF
      ) ||
      userLicence?.status === LicenseStatus.UNLIMITED
    ) {
      return null;
    }

    return (
      <ColoredTag
        color="#FFFFFF"
        bgcolor={
          userLicence?.licenseType === LicenseType.TRIAL
            ? '#AF41AA'
            : userLicence?.licenseDaysLeft && userLicence?.licenseDaysLeft > 0
            ? '#AF41AA'
            : '#FA8C16'
        }
        style={{
          marginInlineEnd: 0,
          padding: '2px 6px',
          fontSize: 11,
          fontWeight: 600,
          marginBottom: 0,
        }}
      >
        {userLicence?.licenseType === LicenseType.TRIAL
          ? t('navigation.subscription-free-trial')
          : userLicence?.licenseDaysLeft && userLicence?.licenseDaysLeft > 0
          ? t('navigation.subscription-subscribed')
          : t('navigation.subscription-overdue')}
      </ColoredTag>
    );
  };

  const renderContextMenu = (record: User) => {
    const handleDeleteUser = () => {
      if (record.userUuid) {
        onDeleteUser(record.userUuid);
      }
    };

    return (
      <div className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          {isCurrentUser && (
            <IconContextButton
              type="text"
              icon={
                <EditIcon
                  style={{ width: '16px', height: '17px', marginRight: '7px' }}
                />
              }
              onClick={openEditDetails}
            >
              {t('organisation.edit-details')}
            </IconContextButton>
          )}
          {currentUser?.accountType &&
            AccountType[currentUser.accountType] === AccountType.OWNER && (
              <IconContextButton
                type="text"
                icon={
                  <UserIcon
                    style={{
                      width: '18px',
                      height: '20px',
                      marginRight: '4px',
                    }}
                  />
                }
                onClick={openChangeOwnerForm}
              >
                {t('users.change-owner')}
              </IconContextButton>
            )}
          {!pwaMobileNav &&
            (!record?.isLicenseOwner &&
            record?.userUuid !== currentUser?.userUuid ? (
              <>
                {currentUser?.canManageUsers && record.userUuid && (
                  <StyledPopconfirm
                    title={t('users.delete-user-title')}
                    description={
                      <span>
                        {t('users.delete-user-description').split('{br}')[0]}
                        <br />
                        {t('users.delete-user-description').split('{br}')[1]}
                      </span>
                    }
                    onConfirm={handleDeleteUser}
                    okText={t('common.yes')}
                    cancelText={t('common.cancel')}
                  >
                    <IconContextButton
                      type="text"
                      icon={
                        <CrossIcon
                          style={{
                            width: '10px',
                            height: '22px',
                            marginRight: '8px',
                            marginLeft: '5px',
                          }}
                        />
                      }
                    >
                      {t('other.delete-account')}
                    </IconContextButton>
                  </StyledPopconfirm>
                )}
              </>
            ) : (
              <StyledPopconfirm
                title={t('other.delete-account-title')}
                description={
                  <>
                    <Typography.Paragraph>
                      {t('other.delete-account-line-1')}
                      <br />
                      {t('other.delete-account-line-2')}
                      <br />
                      {t('other.delete-account-line-3')}
                      <br />
                      {t('other.delete-account-line-4')}
                      <br />
                      {t('other.delete-account-line-5')}
                      <br />
                      {t('other.delete-account-line-6')}
                    </Typography.Paragraph>
                    {t('other.delete-account-confirm')}
                  </>
                }
                okText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={deleteProfile}
              >
                <IconContextButton
                  type="text"
                  icon={
                    <CrossIcon
                      style={{
                        width: '10px',
                        height: '22px',
                        marginRight: '8px',
                        marginLeft: '5px',
                      }}
                    />
                  }
                >
                  {t('other.delete-account')}
                </IconContextButton>
              </StyledPopconfirm>
            ))}
        </Space>
      </div>
    );
  };

  const renderExtraButtons = (record?: User) => {
    if (
      currentUser?.accountType &&
      AccountType[currentUser.accountType] === AccountType.OWNER &&
      currentUser.accountUuid === record?.accountUuid
    ) {
      return (
        <Popover
          placement="topLeft"
          arrow={false}
          trigger="click"
          content={renderContextMenu(record)}
          title={''}
          overlayStyle={{ zIndex: 999 }}
        >
          <ContextButton type="text" aria-label={t('common.more-actions')}>
            <EllipseIcon
              aria-hidden
              focusable="false"
              style={{
                width: 18,
                height: 18,
              }}
            />
          </ContextButton>
        </Popover>
      );
    }
    return null;
  };

  const getFullPhoneNumber = () => {
    let phoneNumber = '';
    if (selectedAccount?.phonePrefix) {
      phoneNumber = selectedAccount.phonePrefix + ' ';
    }
    phoneNumber += selectedAccount?.phoneNumber;
    return phoneNumber;
  };

  const profileContextMenu = renderExtraButtons(selectedAccount);

  const items = [
    {
      label: (
        <span>
          <SafetyOutlined style={{ marginRight: 8 }} />
          {t('users.account-security')}
        </span>
      ),
      key: PROFILE_TAB_ACCOUNT_SECURITY,
      children: renderAccountAndSecurityTab(),
    },
    {
      label: (
        <span>
          <CreditCardOutlined style={{ marginRight: 8 }} />
          {t('users.billing-details')}
        </span>
      ),
      key: PROFILE_TAB_BILLING_DETAILS,
      children: (
        <BillingForm
          billingForm={billingForm}
          countries={countries}
          countriesExternalData={countriesExternalData}
          isCheckoutForm={false}
          changeCurrency={() => {}}
          isLoading={loading}
          isAllowed={isAllowed}
          saveBillingDetails={saveBillingDetails}
          setCountryISO={() => {}}
          isEurCountry={true}
          userVatNumber={userVatNumber}
          setUserVatNumber={setUserVatNumber}
        />
      ),
    },
    {
      label: (
        <span>
          <HistoryOutlined style={{ marginRight: 8 }} />
          {t('users.login-history')}
        </span>
      ),
      key: PROFILE_TAB_LOGIN_HISTORY,
      children: (
        <StyledCard
          $stopshadowbox
          className="profile-settings-card profile-settings-history-card"
          width="100%"
          style={{ marginTop: '25px' }}
        >
          <History
            history={history}
            profileMode={true}
            onClose={() => {}}
            loading={loading}
          />
        </StyledCard>
      ),
    },
  ];

  return (
    <ProfileWrapper className="profile-settings-page">
      <Spin size="large" spinning={loading}>
        <Row>
          <Col span={24}>
            <div className="profile-settings-layout">
              <div className="profile-settings-sidebar">
                <ProfileCard
                  selectedAccount={selectedAccount}
                  currentUser={currentUser}
                  profilePicture={profilePicture}
                  openEditImageWindow={openEditImageWindow}
                  renderUserSubscription={renderUserSubscription}
                  renderRole={renderRole}
                  getFullPhoneNumber={getFullPhoneNumber}
                  redirectPage={redirectPage}
                  openEditDetails={openEditDetails}
                  userLicence={userLicence}
                />
              </div>
              <div className="profile-settings-main">
                {showCard &&
                  currentSubscription?.description !==
                    SubscriptionPlan.ENTERPRISE_PLAN &&
                  currentSubscription?.description !==
                    SubscriptionPlan.LARGE_PLAN &&
                  userLicence?.status !== LicenseStatus.UNLIMITED && (
                    <UpgradeCard
                      showCard={showCard}
                      setShowCard={setShowCard}
                      selectedAccount={selectedAccount}
                      redirectPage={redirectPage}
                      currentSubscription={currentSubscription}
                      getFeaturesOfPlans={getFeaturesOfPlans}
                      userLicence={userLicence}
                    />
                  )}
                <StyledCard
                  className="profile-settings-panel"
                  width="100%"
                  $borderless={pwaMobileNav}
                  $stopshadowbox={pwaMobileNav}
                  bg_color={pwaMobileNav ? 'transparent' : undefined}
                >
                  <div style={{ position: 'relative' }}>
                    {profileContextMenu && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 12,
                          right: 0,
                          zIndex: 2,
                        }}
                      >
                        {profileContextMenu}
                      </div>
                    )}
                    <ScopeTabs
                      className={
                        pwaMobileNav ? 'scope-pwa-account-tabs' : undefined
                      }
                      defaultActiveKey={PROFILE_TAB_ACCOUNT_SECURITY}
                      tabBarStyle={
                        profileContextMenu
                          ? { paddingRight: pwaMobileNav ? 28 : 40 }
                          : undefined
                      }
                      items={
                        isCurrentUser &&
                        AccountType[selectedAccount?.accountType ?? ''] ===
                          AccountType.OWNER &&
                        userLicence?.status !== LicenseStatus.UNLIMITED
                          ? items
                          : items.filter(
                              (item) => item.key !== PROFILE_TAB_BILLING_DETAILS
                            )
                      }
                    />
                  </div>
                </StyledCard>
              </div>
            </div>
            {isCurrentUser && pwaMobileNav ? (
              <div className="profile-settings-logout">
                <ScopeButton
                  type="default"
                  width="100%"
                  height="44px"
                  onClick={() => dispatch(logoutRequest())}
                >
                  <span className="profile-settings-logout__label">
                    <IconLogout
                      className="profile-settings-logout__icon"
                      style={USER_MENU_ICON_STYLE}
                      aria-hidden
                    />
                    {t('navigation.logout')}
                  </span>
                </ScopeButton>
              </div>
            ) : null}
          </Col>
        </Row>
      </Spin>
    </ProfileWrapper>
  );
};
